<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, onMounted, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import PosOrdersApi from 'dashboard/api/posOrders';
import ProductCatalogApi from 'dashboard/api/integrations/productCatalog';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const activeTab = ref('pdv');
const search = ref('');
const sourceFilter = ref('all');
const cart = ref([]);
const catalogProducts = ref([]);
const recentOrders = ref([]);
const selectedOrder = ref(null);
const isLoadingCatalog = ref(false);
const isLoadingOrders = ref(false);
const isSubmittingOrder = ref(false);

const normalizeCents = value => Number(value || 0);

const priceStringToCents = value => {
  if (!value) return 0;
  const numeric = String(value).replace(/[^0-9,.]/g, '');
  const normalized = numeric.includes(',')
    ? numeric.replace(/\./g, '').replace(',', '.')
    : numeric;
  return Math.round(Number(normalized || 0) * 100);
};

const productThumb = product =>
  product.thumbnail ||
  product.thumbnail_url ||
  product.image ||
  product.image_url ||
  product.images?.[0]?.url ||
  product.images?.[0] ||
  product.picture ||
  '';

const sourceOptions = [
  { value: 'all', label: 'Todos canais' },
  { value: 'olist', label: 'Olist' },
  { value: 'feed_xml', label: 'Feed' },
  { value: 'local', label: 'Catálogo local' },
];

const normalizedSource = product => {
  if (product?.source === 'olist') return 'olist';
  if (product?.source === 'feed_xml' || product?.source === 'external') {
    return 'feed_xml';
  }
  return 'local';
};

const sourceLabel = product =>
  sourceOptions.find(option => option.value === normalizedSource(product))
    ?.label || 'Catálogo local';

const sourceBadgeClass = product =>
  ({
    olist: 'bg-purple-50 text-purple-700 ring-purple-100',
    feed_xml: 'bg-amber-50 text-amber-700 ring-amber-100',
    local: 'bg-blue-50 text-blue-700 ring-blue-100',
  })[normalizedSource(product)];

const products = computed(() =>
  catalogProducts.value.map(product => ({
    id: `${normalizedSource(product)}-${
      product.catalog_item_id || product.id || product.retailer_id
    }`,
    catalogItemId: product.catalog_item_id,
    source: normalizedSource(product),
    name: product.name,
    description: product.description,
    priceCents:
      product.price_cents !== undefined
        ? normalizeCents(product.price_cents)
        : priceStringToCents(product.price),
    currency: product.currency || 'BRL',
    itemType: product.item_type || 'product',
    thumbnail: productThumb(product),
    metadata: { external_product: product, source: normalizedSource(product) },
  }))
);

const filteredProducts = computed(() => {
  const query = search.value.trim().toLowerCase();
  if (!query) return [];

  return products.value
    .filter(
      product =>
        sourceFilter.value === 'all' || product.source === sourceFilter.value
    )
    .filter(product =>
      `${product.name} ${product.description || ''}`
        .toLowerCase()
        .includes(query)
    )
    .slice(0, 8);
});

const cartTotal = computed(() =>
  cart.value.reduce((total, item) => total + item.priceCents * item.quantity, 0)
);

const totalSalesCents = computed(() =>
  recentOrders.value
    .filter(
      order => order.status === 'completed' || order.payment_status === 'paid'
    )
    .reduce((total, order) => total + normalizeCents(order.total_cents), 0)
);

const averageTicketCents = computed(() => {
  if (!recentOrders.value.length) return 0;
  return Math.round(
    recentOrders.value.reduce(
      (total, order) => total + normalizeCents(order.total_cents),
      0
    ) / recentOrders.value.length
  );
});

const completedOrdersCount = computed(
  () =>
    recentOrders.value.filter(
      order => order.status === 'completed' || order.payment_status === 'paid'
    ).length
);

const formatCurrency = cents =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(normalizeCents(cents) / 100);

const addToCart = product => {
  const existing = cart.value.find(item => item.id === product.id);
  if (existing) {
    existing.quantity += 1;
  } else {
    cart.value.push({
      ...product,
      quantity: 1,
    });
  }
  search.value = '';
};

const removeFromCart = itemId => {
  cart.value = cart.value.filter(item => item.id !== itemId);
};

const updateQuantity = (itemId, delta) => {
  const item = cart.value.find(record => record.id === itemId);
  if (!item) return;
  item.quantity = Math.max(1, item.quantity + delta);
};

const fetchCatalog = async () => {
  isLoadingCatalog.value = true;
  try {
    const { data } = await ProductCatalogApi.get();
    catalogProducts.value = data.products || [];
  } catch {
    useAlert('Não foi possível carregar o catálogo');
  } finally {
    isLoadingCatalog.value = false;
  }
};

const fetchOrders = async () => {
  isLoadingOrders.value = true;
  try {
    const { data } = await PosOrdersApi.list();
    recentOrders.value = data.payload || [];
  } catch {
    useAlert('Não foi possível carregar os pedidos');
  } finally {
    isLoadingOrders.value = false;
  }
};

const orderPayload = () => ({
  customer_name: 'Consumidor final',
  status: 'open',
  payment_status: 'unpaid',
  payment_method: 'pending',
  currency: 'BRL',
  pos_order_items_attributes: cart.value.map(item => ({
    catalog_item_id: item.catalogItemId,
    name: item.name,
    description: item.description,
    quantity: item.quantity,
    unit_price_cents: item.priceCents,
    currency: item.currency || 'BRL',
    metadata: item.metadata || {},
  })),
});

const createOrder = async () => {
  if (!cart.value.length) {
    useAlert('Adicione itens ao carrinho');
    return;
  }

  isSubmittingOrder.value = true;
  try {
    await PosOrdersApi.create(orderPayload());
    cart.value = [];
    activeTab.value = 'orders';
    await fetchOrders();
    useAlert('Pedido criado');
  } catch {
    useAlert('Não foi possível criar o pedido');
  } finally {
    isSubmittingOrder.value = false;
  }
};

const completeOrder = async order => {
  try {
    await PosOrdersApi.complete(order.id);
    await fetchOrders();
    selectedOrder.value = null;
  } catch {
    useAlert('Não foi possível concluir o pedido');
  }
};

onMounted(() => {
  fetchCatalog();
  fetchOrders();
});
</script>

<template>
  <section
    class="h-full w-full overflow-hidden bg-[#f7f8fb] text-n-slate-12 dark:bg-n-background"
  >
    <main class="flex h-full min-w-0 flex-1 flex-col">
      <header
        class="flex h-[72px] items-center justify-between border-b border-n-weak bg-white/85 px-5 backdrop-blur-xl dark:bg-n-solid-2/85"
      >
        <div class="flex items-center gap-3">
          <button
            type="button"
            class="text-xl font-semibold tracking-[-0.02em] transition-colors"
            :class="
              activeTab === 'pdv'
                ? 'text-n-slate-12'
                : 'text-n-slate-9 hover:text-n-slate-11'
            "
            @click="activeTab = 'pdv'"
          >
            PDV
          </button>
          <button
            type="button"
            class="text-xl font-semibold tracking-[-0.02em] transition-colors"
            :class="
              activeTab === 'orders'
                ? 'text-n-slate-12'
                : 'text-n-slate-9 hover:text-n-slate-11'
            "
            @click="activeTab = 'orders'"
          >
            Pedidos
          </button>
        </div>
        <div class="flex items-center gap-2">
          <button
            v-if="activeTab === 'orders'"
            class="rounded-2xl bg-n-alpha-1 px-4 py-2 text-sm font-medium hover:bg-n-alpha-2"
            @click="fetchOrders"
          >
            Atualizar
          </button>
          <button
            v-else
            class="rounded-2xl bg-n-brand px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="isSubmittingOrder || !cart.length"
            @click="createOrder"
          >
            {{ isSubmittingOrder ? 'Criando...' : 'Receber' }}
          </button>
        </div>
      </header>

      <div v-if="activeTab === 'pdv'" class="min-h-0 flex-1 p-6">
        <section
          class="mx-auto flex h-full max-w-5xl min-w-0 flex-col rounded-[28px] border border-n-weak bg-white shadow-sm dark:bg-n-solid-2"
        >
          <div class="relative border-b border-n-weak p-5">
            <div class="flex gap-2">
              <label
                class="flex h-12 min-w-0 flex-1 items-center gap-3 rounded-2xl border border-n-weak bg-[#fafafa] px-4 text-sm text-n-slate-10 dark:bg-n-solid-3"
              >
                <span class="i-lucide-search size-4" />
                <input
                  v-model="search"
                  class="min-w-0 flex-1 bg-transparent outline-none"
                  placeholder="Buscar produto ou serviço"
                />
                <button
                  type="button"
                  class="grid size-8 place-content-center rounded-xl bg-white text-n-slate-11 shadow-sm dark:bg-n-solid-2"
                >
                  <span class="i-lucide-barcode size-4" />
                </button>
              </label>
              <label
                class="flex h-12 w-44 shrink-0 items-center gap-2 rounded-2xl border border-n-weak bg-[#fafafa] px-3 text-xs text-n-slate-11 dark:bg-n-solid-3"
              >
                <span class="i-lucide-filter size-3.5" />
                <select
                  v-model="sourceFilter"
                  class="reset-base min-w-0 flex-1 cursor-pointer bg-transparent p-0 text-xs font-semibold text-n-slate-12 outline-none"
                >
                  <option
                    v-for="option in sourceOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </label>
            </div>

            <div
              v-if="search.trim() && filteredProducts.length"
              class="absolute inset-x-5 top-[72px] z-20 overflow-hidden rounded-2xl border border-n-weak bg-white p-1 shadow-2xl shadow-slate-900/10 dark:bg-n-solid-2"
            >
              <button
                v-for="item in filteredProducts"
                :key="item.id"
                type="button"
                class="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left hover:bg-n-alpha-1"
                @click="addToCart(item)"
              >
                <span
                  class="grid size-11 shrink-0 place-content-center overflow-hidden rounded-xl bg-blue-50 text-n-brand dark:bg-n-alpha-2"
                >
                  <img
                    v-if="item.thumbnail"
                    :src="item.thumbnail"
                    :alt="item.name"
                    class="size-full object-cover"
                  />
                  <span
                    v-else
                    :class="
                      item.itemType === 'service'
                        ? 'i-lucide-wrench size-5'
                        : 'i-lucide-package size-5'
                    "
                  />
                </span>
                <span class="min-w-0 flex-1">
                  <span class="block truncate text-sm font-semibold">
                    {{ item.name }}
                  </span>
                  <span
                    class="mt-1 inline-flex rounded-md px-1.5 py-0.5 text-[10px] font-semibold ring-1"
                    :class="sourceBadgeClass(item)"
                  >
                    {{ sourceLabel(item) }}
                  </span>
                  <span class="block truncate text-xs text-n-slate-10">
                    {{ item.description || item.source }}
                  </span>
                </span>
                <span class="shrink-0 text-sm font-semibold">
                  {{ formatCurrency(item.priceCents) }}
                </span>
              </button>
            </div>

            <div
              v-else-if="search.trim() && !isLoadingCatalog"
              class="absolute inset-x-5 top-[72px] z-20 rounded-2xl border border-n-weak bg-white px-4 py-5 text-center text-sm text-n-slate-10 shadow-2xl shadow-slate-900/10 dark:bg-n-solid-2"
            >
              Nenhum item encontrado
            </div>
          </div>

          <div class="min-h-0 flex-1 overflow-y-auto p-5">
            <div v-if="isLoadingCatalog" class="grid h-48 place-content-center">
              <Spinner class="text-n-brand" />
            </div>

            <div v-else-if="cart.length" class="space-y-3">
              <div
                v-for="item in cart"
                :key="item.id"
                class="flex items-center gap-4 rounded-2xl border border-n-weak bg-white p-3 shadow-sm dark:bg-n-solid-3"
              >
                <span
                  class="grid size-14 shrink-0 place-content-center overflow-hidden rounded-2xl bg-blue-50 text-n-brand dark:bg-n-alpha-2"
                >
                  <img
                    v-if="item.thumbnail"
                    :src="item.thumbnail"
                    :alt="item.name"
                    class="size-full object-cover"
                  />
                  <span
                    v-else
                    :class="
                      item.itemType === 'service'
                        ? 'i-lucide-wrench size-6'
                        : 'i-lucide-package size-6'
                    "
                  />
                </span>
                <div class="min-w-0 flex-1">
                  <p class="truncate text-sm font-semibold">{{ item.name }}</p>
                  <p class="mt-1 text-xs text-n-slate-10">
                    {{ formatCurrency(item.priceCents) }} un.
                  </p>
                </div>
                <div class="flex items-center gap-2">
                  <button
                    class="grid size-8 place-content-center rounded-full bg-n-alpha-1"
                    @click="updateQuantity(item.id, -1)"
                  >
                    <span class="i-lucide-minus size-3.5" />
                  </button>
                  <span class="w-7 text-center text-sm font-semibold">
                    {{ item.quantity }}
                  </span>
                  <button
                    class="grid size-8 place-content-center rounded-full bg-n-alpha-1"
                    @click="updateQuantity(item.id, 1)"
                  >
                    <span class="i-lucide-plus size-3.5" />
                  </button>
                </div>
                <p class="w-28 text-right text-sm font-semibold">
                  {{ formatCurrency(item.priceCents * item.quantity) }}
                </p>
                <button
                  class="grid size-9 place-content-center rounded-full bg-red-50 text-red-600 dark:bg-red-950/30 dark:text-red-300"
                  @click="removeFromCart(item.id)"
                >
                  <span class="i-lucide-trash-2 size-4" />
                </button>
              </div>
            </div>

            <div
              v-else
              class="flex h-full flex-col items-center justify-center rounded-3xl border border-dashed border-n-weak px-6 py-12 text-center"
            >
              <span
                class="i-lucide-shopping-cart mx-auto block size-9 text-n-slate-9"
              />
              <p class="mt-3 text-sm font-medium">Nenhum item adicionado</p>
              <p class="mt-1 max-w-sm text-sm text-n-slate-10">
                Use a busca para adicionar produtos ou serviços ao pedido.
              </p>
            </div>
          </div>

          <footer
            class="flex items-center justify-between border-t border-n-weak bg-[#fbfbfc] p-5 dark:bg-n-solid-3"
          >
            <div>
              <p class="text-xs text-n-slate-10">Total</p>
              <p class="text-2xl font-semibold">
                {{ formatCurrency(cartTotal) }}
              </p>
            </div>
            <button
              class="rounded-2xl bg-n-brand px-6 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="isSubmittingOrder || !cart.length"
              @click="createOrder"
            >
              {{ isSubmittingOrder ? 'Criando...' : 'Receber' }}
            </button>
          </footer>
        </section>
      </div>

      <div v-else class="min-h-0 flex-1 overflow-y-auto p-6">
        <section class="mx-auto flex max-w-5xl flex-col gap-5">
          <div class="grid grid-cols-3 gap-4">
            <div
              class="rounded-[24px] border border-n-weak bg-white p-5 shadow-sm dark:bg-n-solid-2"
            >
              <p class="text-xs text-n-slate-10">Faturamento</p>
              <p class="mt-2 text-xl font-semibold">
                {{ formatCurrency(totalSalesCents) }}
              </p>
            </div>
            <div
              class="rounded-[24px] border border-n-weak bg-white p-5 shadow-sm dark:bg-n-solid-2"
            >
              <p class="text-xs text-n-slate-10">Ticket médio</p>
              <p class="mt-2 text-xl font-semibold">
                {{ formatCurrency(averageTicketCents) }}
              </p>
            </div>
            <div
              class="rounded-[24px] border border-n-weak bg-white p-5 shadow-sm dark:bg-n-solid-2"
            >
              <p class="text-xs text-n-slate-10">Pedidos pagos</p>
              <p class="mt-2 text-xl font-semibold">
                {{ completedOrdersCount }}
              </p>
            </div>
          </div>

          <div
            class="rounded-[28px] border border-n-weak bg-white p-5 shadow-sm dark:bg-n-solid-2"
          >
            <div class="mb-4 flex items-center justify-between">
              <h2 class="text-sm font-semibold">Pedidos</h2>
              <span v-if="isLoadingOrders" class="text-xs text-n-slate-10">
                Carregando
              </span>
            </div>
            <div class="space-y-2">
              <button
                v-for="order in recentOrders"
                :key="order.id"
                class="w-full rounded-2xl border border-transparent bg-[#fafafa] p-4 text-left transition-colors hover:border-n-brand/20 dark:bg-n-solid-3"
                @click="selectedOrder = order"
              >
                <div class="flex items-center justify-between gap-3">
                  <div class="min-w-0">
                    <p class="truncate text-sm font-semibold">
                      {{ order.customer_name || 'Consumidor final' }}
                    </p>
                    <p class="text-xs text-n-slate-10">
                      #{{ order.id }} · {{ order.status }} ·
                      {{ order.payment_status }}
                    </p>
                  </div>
                  <p class="shrink-0 text-sm font-semibold">
                    {{ formatCurrency(order.total_cents) }}
                  </p>
                </div>
              </button>
            </div>
          </div>
        </section>
      </div>
    </main>

    <div
      v-if="selectedOrder"
      class="fixed inset-0 z-50 grid place-items-center bg-slate-950/30 p-6 backdrop-blur-sm"
      @click.self="selectedOrder = null"
    >
      <section
        class="w-full max-w-lg rounded-[28px] border border-white/70 bg-white p-5 shadow-2xl shadow-slate-950/20 dark:border-n-weak dark:bg-n-solid-2"
      >
        <header class="flex items-center justify-between gap-4">
          <div>
            <p class="text-xs font-medium text-n-slate-10">Pedido</p>
            <h2 class="text-xl font-semibold">#{{ selectedOrder.id }}</h2>
          </div>
          <button
            class="grid size-9 place-content-center rounded-full bg-n-alpha-1 text-n-slate-11"
            @click="selectedOrder = null"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </header>

        <div class="mt-5 grid gap-3 text-sm">
          <div class="flex justify-between rounded-2xl bg-n-alpha-1 px-4 py-3">
            <span class="text-n-slate-10">Cliente</span>
            <span class="font-medium">
              {{ selectedOrder.customer_name || 'Consumidor final' }}
            </span>
          </div>
          <div class="flex justify-between rounded-2xl bg-n-alpha-1 px-4 py-3">
            <span class="text-n-slate-10">Status</span>
            <span class="font-medium">{{ selectedOrder.status }}</span>
          </div>
          <div class="flex justify-between rounded-2xl bg-n-alpha-1 px-4 py-3">
            <span class="text-n-slate-10">Pagamento</span>
            <span class="font-medium">{{ selectedOrder.payment_status }}</span>
          </div>
          <div
            class="flex justify-between rounded-2xl bg-n-slate-12 px-4 py-3 text-white"
          >
            <span class="text-white/60">Total</span>
            <span class="font-semibold">
              {{ formatCurrency(selectedOrder.total_cents) }}
            </span>
          </div>
        </div>

        <button
          v-if="selectedOrder.status !== 'completed'"
          class="mt-5 h-11 w-full rounded-2xl bg-emerald-600 text-sm font-semibold text-white"
          @click="completeOrder(selectedOrder)"
        >
          Concluir pedido
        </button>
      </section>
    </div>
  </section>
</template>
